import { useFormContext } from 'react-hook-form';
import { Input, Textarea, Select, Button } from '../../../components/ui';

const CATEGORIES = [
  { value: 'electronics', label: 'Electronics' },
  { value: 'furniture', label: 'Furniture' },
  { value: 'gaming', label: 'Gaming' },
  { value: 'fashion', label: 'Fashion' },
  { value: 'sports', label: 'Sports' },
  { value: 'collectibles', label: 'Collectibles' },
  { value: 'home', label: 'Home & Garden' },
  { value: 'toys', label: 'Toys' },
  { value: 'music', label: 'Music & Instruments' },
  { value: 'books', label: 'Books & Media' },
  { value: 'vehicles', label: 'Vehicles' },
  { value: 'other', label: 'Other' },
];

const CONDITIONS = [
  { value: 'new', label: 'New', description: 'Unused, still in packaging' },
  { value: 'likeNew', label: 'Like New', description: 'Used once or twice, no visible wear' },
  { value: 'good', label: 'Good', description: 'Used but well maintained' },
  { value: 'fair', label: 'Fair', description: 'Shows wear, fully functional' },
];

/**
 * Step 2: Details
 *
 * Title, description, category, and condition.
 */
export default function StepDetails({ onNext, onBack }) {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
    trigger,
  } = useFormContext();

  const title = watch('title');
  const description = watch('description');
  const category = watch('category');
  const condition = watch('condition');

  const isValid = title?.length >= 5 && description?.length >= 20 && category && condition;

  async function handleContinue() {
    const valid = await trigger(['title', 'description', 'category', 'condition']);
    if (valid) {
      onNext();
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-display text-cream mb-2">
          Item Details
        </h1>
        <p className="text-cream/60">
          Describe your item to help buyers understand what they're entering for.
        </p>
      </div>

      {/* Form */}
      <div className="space-y-6 mb-8">
        {/* Title */}
        <div>
          <Input
            label="Title"
            placeholder="e.g., iPhone 14 Pro 256GB Space Black"
            maxLength={80}
            showCharCount
            error={errors.title?.message}
            {...register('title')}
          />
        </div>

        {/* Description */}
        <div>
          <Textarea
            label="Description"
            placeholder="Describe your item in detail. Include brand, model, condition notes, what's included, etc."
            rows={5}
            maxLength={2000}
            showCharCount
            error={errors.description?.message}
            {...register('description')}
          />
        </div>

        {/* Category */}
        <div>
          <Select
            label="Category"
            placeholder="Select a category"
            options={CATEGORIES}
            value={category}
            onChange={(e) => setValue('category', e.target.value, { shouldValidate: true })}
            error={errors.category?.message}
          />
        </div>

        {/* Condition */}
        <div>
          <label className="block text-sm font-medium text-cream mb-3">
            Condition
          </label>
          <div className="grid grid-cols-2 gap-3">
            {CONDITIONS.map((cond) => (
              <button
                key={cond.value}
                type="button"
                onClick={() => setValue('condition', cond.value, { shouldValidate: true })}
                className={`p-4 rounded-lg border-2 text-left transition-colors ${
                  condition === cond.value
                    ? 'border-primary bg-primary/10'
                    : 'border-midnight-lighter hover:border-cream/30'
                }`}
              >
                <span className={`font-semibold block ${
                  condition === cond.value ? 'text-primary' : 'text-cream'
                }`}>
                  {cond.label}
                </span>
                <span className="text-cream/50 text-sm">{cond.description}</span>
              </button>
            ))}
          </div>
          {errors.condition?.message && (
            <p className="text-danger text-sm mt-2">{errors.condition.message}</p>
          )}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between gap-4">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button onClick={handleContinue} disabled={!isValid}>
          Continue
        </Button>
      </div>
    </div>
  );
}
